import React, { useContext, useState } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip } from '@mui/material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ContextElementFunctions from '../../Contexts/ContextElementFunctions';
import ContextViewFileList from '../../Contexts/ContextViewFileList';
import { DocumentsService } from '../../../services/openapi';
import APIWrapperContext from '../../../services/APIWrapperContext';
import { restore_slashes } from '../../background_functions';

export const RenameItem = () => {
  const { call_backend_api } = useContext(APIWrapperContext);
  const { dir_id, type, name } = useContext(ContextElementFunctions); // dir_id = item key
  const { curr_dir_id, curr_dir_view, update_dir_view, invalidate_dir_cache } = useContext(ContextViewFileList);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Match exactly what the row displays as the item name (FileListItem renders restore_slashes(el.parent)).
  const currentName = restore_slashes(name);
  const [newName, setNewName] = useState(currentName);

  const siblingNames: string[] =
    curr_dir_view && 'children' in (curr_dir_view as any) && Array.isArray((curr_dir_view as any).children)
      ? (curr_dir_view as any).children
          .filter((c: any) => c.hidrive_id !== dir_id)
          .map((c: any) => restore_slashes(c.parent))
      : [];

  const trimmed = newName.trim();
  const invalidChars = /[/\\]|\.\./.test(trimmed);
  const isUnchanged = trimmed === currentName;
  const isDuplicate = siblingNames.includes(trimmed);
  const errorText = !trimmed ? 'Name darf nicht leer sein'
    : invalidChars ? 'Ungültige Zeichen (/ \\ ..)'
    : isDuplicate ? 'Name existiert bereits' : '';
  const canSubmit = !!trimmed && !invalidChars && !isUnchanged && !isDuplicate && !loading && !!curr_dir_id;

  const handleOpen = (e: React.MouseEvent) => { e.stopPropagation(); setNewName(currentName); setOpen(true); };
  const handleClose = () => { if (!loading) setOpen(false); };

  const handleRename = () => {
    if (!canSubmit) return;
    setLoading(true);
    call_backend_api(
      DocumentsService.moveDocumentDocMovePost(dir_id, trimmed, curr_dir_id!, curr_dir_id!),
      () => { setLoading(false); setOpen(false); invalidate_dir_cache(); update_dir_view(curr_dir_id); },
      () => { setLoading(false); },
      undefined,
      true,
      `${type === 'file' ? 'Datei' : 'Ordner'} umbenannt!`,
      `${type === 'file' ? 'Datei' : 'Ordner'} konnte nicht umbenannt werden!`
    );
  };

  return (
    <>
      <Tooltip title='Umbenennen'>
        <IconButton onClick={handleOpen} color='primary' aria-label='rename'>
          <DriveFileRenameOutlineIcon />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={handleClose} onClick={(e) => e.stopPropagation()} maxWidth='sm' fullWidth>
        <DialogTitle>{type === 'file' ? 'Datei umbenennen' : 'Ordner umbenennen'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin='dense' fullWidth variant='standard' label='Neuer Name'
            value={newName} onChange={(e) => setNewName(e.target.value)}
            error={!!errorText && trimmed.length > 0} helperText={trimmed.length > 0 ? errorText : ''}
            disabled={loading}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) handleRename(); }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>Abbrechen</Button>
          <Button
            onClick={handleRename}
            variant='contained'
            disabled={!canSubmit}
            aria-busy={loading}
            startIcon={loading ? <CircularProgress size={16} color='inherit'/> : undefined}
            sx={{minWidth: 148}}>
            {loading ? 'Wird umbenannt…' : 'Umbenennen'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
